import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, EyeOff } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { useLanguage } from '../context/LanguageContext';
import { useTheme } from '../context/ThemeContext';
import { authService } from '../services/auth';

const Login = () => {
  const [email, setEmail]           = useState('');
  const [password, setPassword]     = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [showPass, setShowPass]     = useState(false);
  const [loading, setLoading]       = useState(false);
  const navigate  = useNavigate();
  const { toast } = useToast();
  const { t }     = useLanguage();
  const { theme } = useTheme();
  const isDark    = theme === 'dark';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await authService.login(email, password, rememberMe);
      toast({ title: t('login.loginSuccess'), description: t('login.welcomeMessage') });
      navigate('/dashboard');
    } catch {
      toast({ title: t('login.loginFailed'), description: t('login.invalidCredentials'), variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = {
    width: '100%',
    background: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(99,102,241,0.03)',
    border: isDark ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(99,102,241,0.15)',
    borderRadius: '12px',
    padding: '12px 16px',
    fontSize: '14px',
    fontFamily: 'Inter, sans-serif',
    color: isDark ? '#f1f5f9' : '#0f172a',
    outline: 'none',
    transition: 'border-color 0.2s, box-shadow 0.2s',
    boxSizing: 'border-box',
  };

  const handleInputFocus = (e) => {
    e.target.style.borderColor = 'rgba(99,102,241,0.5)';
    e.target.style.boxShadow = '0 0 0 3px rgba(99,102,241,0.1)';
  };
  const handleInputBlur = (e) => {
    e.target.style.borderColor = isDark ? 'rgba(255,255,255,0.08)' : 'rgba(99,102,241,0.15)';
    e.target.style.boxShadow = 'none';
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center p-4 relative overflow-hidden"
      style={{ background: isDark ? '#04060d' : '#f0f4ff' }}
    >
      {/* Background grid */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        {/* Grid pattern */}
        {isDark && (
          <div style={{
            position: 'absolute', inset: 0,
            backgroundImage: `
              linear-gradient(rgba(99,102,241,0.04) 1px, transparent 1px),
              linear-gradient(90deg, rgba(99,102,241,0.04) 1px, transparent 1px)
            `,
            backgroundSize: '48px 48px',
          }} />
        )}
      </div>

      {/* Card */}
      <div
        className="relative w-full max-w-md animate-scale-in"
        style={{ animationDuration: '0.35s' }}
      >
        <div
          className="rounded-xl p-8"
          style={{
            background: isDark ? 'rgba(8,12,22,0.85)' : 'rgba(255,255,255,0.9)',
            backdropFilter: 'blur(24px)',
            WebkitBackdropFilter: 'blur(24px)',
            border: isDark ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(99,102,241,0.15)',
            boxShadow: isDark
              ? '0 32px 80px rgba(0,0,0,0.6), 0 0 0 1px rgba(255,255,255,0.03)'
              : '0 32px 80px rgba(99,102,241,0.12), 0 0 0 1px rgba(99,102,241,0.05)',
          }}
        >
          {/* Header */}
          <div className="mb-8">
            <div className="flex items-center justify-center mb-6">
              <img
                src={`${process.env.PUBLIC_URL}/logo.webp`}
                alt="Abramov CRM"
                className="h-24 w-60 object-contain"
              />
            </div>
            <h1 className="text-2xl font-bold tracking-tight text-center" style={{ color: isDark ? '#f1f5f9' : '#0f172a' }}>
              {t('login.title')}
            </h1>
            <p className="text-sm mt-1 text-center" style={{ color: isDark ? 'rgba(255,255,255,0.3)' : '#94a3b8' }}>
              Sign in to your workspace
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Email */}
            <div>
              <label
                className="block text-xs font-semibold uppercase tracking-wider mb-2"
                style={{ color: isDark ? 'rgba(255,255,255,0.4)' : '#64748b' }}
              >
                {t('login.email')}
              </label>
              <input
                type="email"
                placeholder="you@company.com"
                value={email}
                onChange={e => setEmail(e.target.value)}
                onFocus={handleInputFocus}
                onBlur={handleInputBlur}
                style={inputStyle}
                required
              />
            </div>

            {/* Password */}
            <div>
              <label
                className="block text-xs font-semibold uppercase tracking-wider mb-2"
                style={{ color: isDark ? 'rgba(255,255,255,0.4)' : '#64748b' }}
              >
                {t('login.password')}
              </label>
              <div className="relative">
                <input
                  type={showPass ? 'text' : 'password'}
                  placeholder="**********"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  onFocus={handleInputFocus}
                  onBlur={handleInputBlur}
                  style={{ ...inputStyle, paddingRight: '48px' }}
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPass(v => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 w-8 h-8 flex items-center justify-center rounded-lg transition-all duration-200"
                  style={{ color: isDark ? 'rgba(255,255,255,0.3)' : '#94a3b8' }}
                  onMouseEnter={e => { e.currentTarget.style.color = isDark ? 'rgba(255,255,255,0.7)' : '#4f46e5'; e.currentTarget.style.background = isDark ? 'rgba(255,255,255,0.05)' : 'rgba(99,102,241,0.07)'; }}
                  onMouseLeave={e => { e.currentTarget.style.color = isDark ? 'rgba(255,255,255,0.3)' : '#94a3b8'; e.currentTarget.style.background = 'transparent'; }}
                >
                  {showPass ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            {/* Remember & Forgot */}
            <div className="flex items-center justify-between">
              <label className="flex items-center gap-2.5 cursor-pointer group">
                <div className="relative">
                  <input
                    type="checkbox"
                    checked={rememberMe}
                    onChange={e => setRememberMe(e.target.checked)}
                    className="sr-only"
                  />
                  <div
                    className="w-4 h-4 rounded flex items-center justify-center transition-all duration-200"
                    style={{
                      background: rememberMe
                        ? 'linear-gradient(135deg, #6366f1, #8b5cf6)'
                        : (isDark ? 'rgba(255,255,255,0.06)' : 'rgba(99,102,241,0.06)'),
                      border: rememberMe
                        ? '1px solid transparent'
                        : (isDark ? '1px solid rgba(255,255,255,0.12)' : '1px solid rgba(99,102,241,0.2)'),
                      boxShadow: rememberMe ? '0 0 8px rgba(99,102,241,0.4)' : 'none',
                    }}
                    onClick={() => setRememberMe(v => !v)}
                  >
                    {rememberMe && (
                      <svg width="9" height="7" viewBox="0 0 9 7" fill="none">
                        <path d="M1 3.5L3.5 6L8 1" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    )}
                  </div>
                </div>
                <span className="text-xs font-medium" style={{ color: isDark ? 'rgba(255,255,255,0.4)' : '#64748b' }}>
                  {t('login.rememberMe')}
                </span>
              </label>
              <button
                type="button"
                className="text-xs font-medium transition-colors"
                style={{ color: '#818cf8' }}
                onMouseEnter={e => { e.currentTarget.style.color = '#a5b4fc'; }}
                onMouseLeave={e => { e.currentTarget.style.color = '#818cf8'; }}
              >
                {t('login.forgotPassword')}
              </button>
            </div>

            {/* Submit */}
            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 rounded-lg text-sm font-semibold text-white transition-all duration-200 mt-2"
              style={{
                background: loading
                  ? 'rgba(99,102,241,0.5)'
                  : 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                boxShadow: loading ? 'none' : '0 0 20px rgba(99,102,241,0.35), 0 4px 12px rgba(99,102,241,0.2)',
                cursor: loading ? 'not-allowed' : 'pointer',
                transform: 'translateY(0)',
              }}
              onMouseEnter={e => { if (!loading) { e.currentTarget.style.boxShadow = '0 0 28px rgba(99,102,241,0.5), 0 6px 20px rgba(99,102,241,0.3)'; e.currentTarget.style.transform = 'translateY(-1px)'; } }}
              onMouseLeave={e => { if (!loading) { e.currentTarget.style.boxShadow = '0 0 20px rgba(99,102,241,0.35), 0 4px 12px rgba(99,102,241,0.2)'; e.currentTarget.style.transform = 'translateY(0)'; } }}
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <span
                    className="w-4 h-4 rounded-full border-2 border-white/30"
                    style={{ borderTopColor: 'white', animation: 'spin 0.7s linear infinite' }}
                  />
                  Signing in...
                </span>
              ) : t('login.signIn')}
            </button>
          </form>
        </div>
      </div>

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

export default Login;
